import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import {
  AlertTriangle,
  ArrowRight,
  CreditCard,
  DollarSign,
  Loader2,
  RefreshCw,
  Rocket,
  Sparkles,
  Users,
} from "lucide-react";
import PartnerPageHeader from "../components/PartnerPageHeader";
import PartnerKpiCard from "../components/PartnerKpiCard";
import PartnerPoolKpi from "../components/PartnerPoolKpi";
import { formatAiCredits } from "@/lib/formatAiCredits";
import { AI_TIERS } from "@/constants/aiTiersCatalog";
import { countActiveClients, mapApiRosterRow, sumActiveMargin } from "@/lib/partnerMargin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BILLING_PILL = {
  ready: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  past_due: "bg-red-500/10 text-red-400 border-red-500/30",
  inactive: "bg-white/5 text-partner-muted border-partner",
};

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const OverviewSkeleton = () => (
  <div className="space-y-8 animate-pulse" data-testid="partner-overview-skeleton">
    <div className="h-24 rounded-2xl border border-partner bg-[#11151F]" />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-32 rounded-2xl border border-partner bg-[#11151F]" />
      ))}
    </div>
  </div>
);

const LoadError = ({ message, onRetry, isRetrying }) => (
  <div
    className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center"
    data-testid="partner-overview-load-error"
  >
    <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-4" />
    <h2 className="text-xl font-bold text-partner mb-2">Couldn&apos;t load dashboard</h2>
    <p className="text-sm text-partner-muted font-body max-w-md mx-auto">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      disabled={isRetrying}
      className="mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-white/10 text-partner hover:bg-white/20 disabled:opacity-50"
      data-testid="partner-overview-retry-button"
    >
      {isRetrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
      Retry
    </button>
  </div>
);

const PartnerOverview = () => {
  const email = Cookies.get("email") || "";
  const [poolSummary, setPoolSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isFatalError, setIsFatalError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const getAuthHeader = useCallback(() => getPartnerAuthHeaders(), []);

  const fetchDashboard = useCallback(async () => {
    setLoadError("");
    setIsFatalError(false);
    try {
      const headers = getAuthHeader();
      const [poolResult, clientsResult, billingResult] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/partner/ai-usage/pool`, { headers }),
        axios.get(`${BACKEND_URL}/api/partner/clients`, { headers }),
        axios.get(`${BACKEND_URL}/api/partner/billing/summary`, { headers }),
      ]);

      const errors = [poolResult, clientsResult, billingResult].filter(
        (result) => result.status === "rejected"
      );
      if (errors.length === 3) {
        throw errors[0].reason;
      }

      if (poolResult.status === "fulfilled") {
        setPoolSummary(poolResult.value.data);
      } else {
        setPoolSummary(null);
      }

      if (clientsResult.status === "fulfilled") {
        const rows = clientsResult.value.data?.clients || [];
        setClients(rows.map(mapApiRosterRow));
      } else {
        setClients([]);
      }

      if (billingResult.status === "fulfilled") {
        setBillingSummary(billingResult.value.data);
      } else {
        setBillingSummary(null);
      }

      if (errors.length > 0) {
        setLoadError(
          extractErrorMessage(
            errors[0].reason,
            "Some dashboard data could not be loaded. Partial results are shown."
          )
        );
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        window.location.href = "/login";
        return;
      }
      setPoolSummary(null);
      setClients([]);
      setBillingSummary(null);
      setIsFatalError(true);
      setLoadError(
        extractErrorMessage(err, "Could not load your partner dashboard. Please try again.")
      );
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!getAuthHeader().Authorization) return;
    setIsLoading(true);
    fetchDashboard().finally(() => setIsLoading(false));
  }, [fetchDashboard, getAuthHeader]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setIsLoading(true);
    await fetchDashboard();
    setIsRetrying(false);
    setIsLoading(false);
  };

  const activeClientCount = useMemo(() => countActiveClients(clients), [clients]);
  const estimatedMargin = useMemo(() => sumActiveMargin(clients), [clients]);
  const billingStatus = billingSummary?.status || "inactive";
  const wholesaleDue = billingSummary?.estimated_monthly_total ?? 0;
  const hasLoaded = !isLoading && !isFatalError;

  const poolProps = poolSummary
    ? {
        included: poolSummary.pool_included,
        used: poolSummary.pool_used,
        clientCount: poolSummary.client_count,
        usagePercent: poolSummary.usage_percent,
        trackingLive: poolSummary.tracking_live !== false,
      }
    : {
        included: 0,
        used: 0,
        clientCount: 0,
        usagePercent: 0,
        trackingLive: true,
      };

  return (
    <div data-testid="partner-overview">
      <PartnerPageHeader
        tag="Wholesale deployment console"
        title="Welcome back."
        titleAccent="Your partner overview"
        subtitle={
          <>
            Signed in as <span className="text-partner-accent">{email}</span>. Review margins,
            deploy clients, and manage your roster from here.
          </>
        }
      />

      {isLoading && <OverviewSkeleton />}

      {!isLoading && isFatalError && (
        <LoadError message={loadError} onRetry={handleRetry} isRetrying={isRetrying} />
      )}

      {!isLoading && !isFatalError && (
        <>
          {loadError && (
            <div
              className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 flex items-start gap-3"
              data-testid="partner-overview-partial-error"
            >
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-partner-muted font-body">{loadError}</p>
            </div>
          )}

          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs font-mono text-partner-muted uppercase tracking-wider">
              Billing status
            </span>
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-body border capitalize ${
                BILLING_PILL[billingStatus] || BILLING_PILL.inactive
              }`}
            >
              {billingStatus.replace("_", " ")}
            </span>
          </div>

          <PartnerPoolKpi pool={poolProps} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <PartnerKpiCard
              label="Active clients"
              value={hasLoaded ? activeClientCount : "…"}
              sub="Deployed under your brand"
              icon={Users}
            />
            <PartnerKpiCard
              label="Client AI credits used"
              value={hasLoaded ? formatAiCredits(poolProps.used) : "…"}
              sub={
                hasLoaded
                  ? `${poolProps.clientCount} clients in pool`
                  : "Pool usage across eligible clients"
              }
              icon={Sparkles}
            />
            <PartnerKpiCard
              label="Est. monthly margin"
              value={hasLoaded ? `$${estimatedMargin.toLocaleString()}` : "…"}
              sub="Sum of active clients (custom + estimated retail)"
              icon={DollarSign}
            />
            <PartnerKpiCard
              label="Wholesale due"
              value={hasLoaded ? `$${wholesaleDue.toLocaleString()}` : "…"}
              sub={`Tier 1×$${AI_TIERS[0].wholesale} · Tier 2×$${AI_TIERS[1].wholesale} · Tier 3×$${AI_TIERS[2].wholesale}`}
              icon={CreditCard}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/partner/deploy"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 transition-colors"
            >
              <Rocket className="w-4 h-4" />
              Deploy client
            </Link>
            <Link
              to="/partner/tiers"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:border-[#00D2FF]/30 transition-colors"
            >
              Review tiers
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/partner/clients"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:border-[#00D2FF]/30 transition-colors"
            >
              View all clients
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerOverview;
